/**
 * 数据分析 › 指标统计（`#stats`，后台页面对齐批 4 第 5 项；只读）：回答「时间统计不真实，实际感受慢很多」（摸底 B 第 17 条）。
 * 原先能看到的只有单轮耗时（中位约 2 秒）和单次试跑中位（约 13 秒），用户等的是整批（十几到二十几分钟）。三个页签：
 * - 整批耗时：最近 N 批的墙钟（第一个试跑开始 → 最后一个结束）、排队等待、单次试跑 p50／p90／最长、花费、未判定（按原因，限流单列）；
 *   点一行开抽屉看分题族耗时与最慢的 5 次试跑（点一次试跑打开它的运行轨迹）。
 * - 耗时分段：所选批次（或近 7 天全部批次）的试跑耗时之和拆成 被测 agent 各节点、模拟客户、判官、CX、未记账，看清慢在哪。
 * - 单轮耗时：近 N 天每轮端到端耗时（人工提问和评测都算）与分节点每轮耗时。
 * 口径写在页头说明；后端 `GET /stats/*`（`api/stats.py`，口径见 `eval/timing.py`）。
 */
import { useNavigate } from "@tanstack/react-router";
import { type ReactNode, useMemo, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { type BarSegment, CHART, SegmentBar } from "@/components/ui/charts";
import { Drawer, DrawerSection } from "@/components/ui/drawer";
import { EmptyState, LoadingInline } from "@/components/ui/feedback";
import { LabeledSelect } from "@/components/ui/select";
import { KpiCard } from "@/components/ui/stats";
import { Tabs } from "@/components/ui/tabs";
import { planErrorMessage } from "@/lib/api/plan-lab";
import {
	type PlanStatsBatch,
	type PlanStatsFamily,
	type PlanStatsSegmentRow,
	type PlanStatsSlowTrial,
	type PlanStatsTurns,
	usePlanStatsBatch,
	usePlanStatsBatches,
	usePlanStatsSegments,
	usePlanStatsTurns,
} from "@/lib/api/plan-stats";

import { OutcomeBadge, TrialDrawer } from "../eval/batch-report";
import { FailState } from "../kit/fail-state";
import { MetricTip } from "../kit/metric-tip";
import { PageJump } from "../kit/page-jump";
import { BATCH_STATUS_TONE, BATCH_STATUS_ZH, UNDETERMINED_ZH, zh } from "../kit/plan-labels";
import { type PlanColumn, PlanTable } from "../kit/plan-table";

type View = "batches" | "segments" | "turns";
const VIEWS: { key: View; label: string }[] = [
	{ key: "batches", label: "整批耗时" },
	{ key: "segments", label: "耗时分段" },
	{ key: "turns", label: "单轮耗时" },
];
const DAYS_ALL = "__days7";

/* ── 格式 ─────────────────────────────────────────────── */

/** 秒 → 「12.9 秒」「17 分 18 秒」「1 时 02 分」 */
export function fmtS(s: number | null | undefined): string {
	if (s == null) return "—";
	if (s < 59.95) return `${s.toFixed(1)} 秒`;
	const r = Math.round(s);
	const m = Math.floor(r / 60);
	if (m < 60) return `${m} 分 ${String(r % 60).padStart(2, "0")} 秒`;
	return `${Math.floor(m / 60)} 时 ${String(m % 60).padStart(2, "0")} 分`;
}

/** 毫秒 → 「560 毫秒」「1.86 秒」 */
export function fmtMs(ms: number | null | undefined): string {
	if (ms == null) return "—";
	if (ms < 1000) return `${Math.round(ms)} 毫秒`;
	return fmtS(ms / 1000);
}

function fmtPct(v: number | null | undefined): string {
	return v == null ? "—" : `${(v * 100).toFixed(1)}%`;
}

function usd(v: number | null | undefined): string {
	return v == null ? "—" : `$${v.toFixed(v >= 1 ? 2 : 3)}`;
}

function at(s: string | null | undefined): string {
	if (!s) return "—";
	const d = new Date(s);
	if (Number.isNaN(d.getTime())) return s;
	const p = (n: number) => String(n).padStart(2, "0");
	return `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

const tail = (id: string) => id.slice(-4);

/** 未判定按原因：「限流 2 · 超时 1」（限流单列，10-21 A3） */
function undetText(r: Record<string, number> | null | undefined): string {
	return Object.entries(r ?? {})
		.filter(([, n]) => n > 0)
		.map(([k, n]) => `${zh(UNDETERMINED_ZH, k)} ${n}`)
		.join(" · ");
}

/** 测试集名；退役集（判分规则 EV-26）标「已退役」 */
function setName(b: Pick<PlanStatsBatch, "test_set" | "test_set_retired">): string | null {
	return b.test_set ? `${b.test_set}${b.test_set_retired ? "（已退役）" : ""}` : null;
}

function batchLabel(b: PlanStatsBatch): string {
	return `${tail(b.batch_id)} · ${setName(b) ?? `${b.scenario_count} 题`} · ${at(b.created_at)}`;
}

function Muted({ children }: { children: ReactNode }) {
	return <span className="text-xs text-text-muted">{children}</span>;
}

function Block({
	title,
	note,
	children,
	testId,
}: {
	title?: string;
	note?: ReactNode;
	children: ReactNode;
	testId?: string;
}) {
	return (
		<Card data-testid={testId}>
			<CardContent className="space-y-3 py-4">
				{title ? (
					<div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
						<h3 className="text-sm font-semibold text-text-primary">{title}</h3>
						{note ? <Muted>{note}</Muted> : null}
					</div>
				) : null}
				{children}
			</CardContent>
		</Card>
	);
}

function QueryState({
	q,
	testId,
}: {
	q: { isLoading: boolean; error: unknown; refetch: () => unknown; isFetching: boolean };
	testId: string;
}) {
	return (
		<Card>
			<CardContent className={q.isLoading ? "py-4" : undefined}>
				{q.isLoading ? (
					<LoadingInline text="统计中…" />
				) : (
					<FailState
						reason={planErrorMessage(q.error)}
						onRetry={() => q.refetch()}
						retrying={q.isFetching}
						testId={testId}
					/>
				)}
			</CardContent>
		</Card>
	);
}

/* ── 页面 ─────────────────────────────────────────────── */

export function StatsTab() {
	const [view, setView] = useState<View>("batches");
	const [segScope, setSegScope] = useState<string | null>(null);
	return (
		<div className="space-y-3" data-testid="plan-block-stats">
			<Tabs items={VIEWS} active={view} onChange={setView} />
			{view === "batches" ? (
				<BatchesPanel
					onSegments={(id) => {
						setSegScope(id);
						setView("segments");
					}}
				/>
			) : null}
			{view === "segments" ? <SegmentsPanel scope={segScope} onScope={setSegScope} /> : null}
			{view === "turns" ? <TurnsPanel /> : null}
		</div>
	);
}

/* ── ① 整批耗时 ─────────────────────────────────────── */

const BATCH_COLUMNS: PlanColumn<PlanStatsBatch>[] = [
	{
		key: "batch",
		title: "批次",
		className: "w-28",
		render: (b) => (
			<span className="whitespace-nowrap" title={b.batch_id}>
				<span className="font-mono">{tail(b.batch_id)}</span>{" "}
				<span className="text-xs text-text-muted">{at(b.created_at)}</span>
			</span>
		),
	},
	{
		key: "status",
		title: "状态",
		className: "w-20",
		render: (b) => (
			<Badge tone={BATCH_STATUS_TONE[b.status ?? ""] ?? "muted"}>
				{b.kind === "ab" ? "对比 · " : ""}
				{zh(BATCH_STATUS_ZH, b.status)}
			</Badge>
		),
	},
	{
		key: "set",
		title: "测试集 · 题数",
		className: "max-w-[160px]",
		render: (b) => (
			<span className="block truncate" title={b.note ?? undefined}>
				{setName(b) ?? "自选"} · {b.scenario_count}
			</span>
		),
	},
	{
		key: "trials",
		title: "试跑",
		align: "right",
		render: (b) => (
			<span className="font-mono">
				{b.ran}
				<span className="text-text-muted">/{b.total_trials}</span>
			</span>
		),
	},
	{
		key: "conc",
		title: "并发",
		align: "right",
		render: (b) => <span className="font-mono">{b.concurrency}</span>,
	},
	{
		key: "wall",
		title: <MetricTip id="stats.wall_s">墙钟</MetricTip>,
		align: "right",
		render: (b) => <span className="font-semibold">{fmtS(b.wall_s)}</span>,
	},
	{
		key: "queue",
		title: <MetricTip id="stats.queue_wait_s">排队等待</MetricTip>,
		align: "right",
		render: (b) => (
			<span className={b.queue_wait_s && b.queue_wait_s >= 60 ? "text-brand-warning" : ""}>
				{fmtS(b.queue_wait_s)}
			</span>
		),
	},
	{
		key: "p",
		title: <MetricTip id="stats.trial_p">单次 p50 ／ p90 ／ 最长</MetricTip>,
		align: "right",
		render: (b) =>
			b.ran ? (
				<span className="whitespace-nowrap">
					{fmtS(b.trial_p50_s)} ／ {fmtS(b.trial_p90_s)} ／ {fmtS(b.trial_max_s)}
				</span>
			) : (
				<span className="text-text-muted">没跑</span>
			),
	},
	{
		key: "cost",
		title: <MetricTip id="stats.cost_usd">花费</MetricTip>,
		align: "right",
		render: (b) => <span className="font-mono">{usd(b.cost_usd)}</span>,
	},
	{
		key: "und",
		title: <MetricTip id="stats.undetermined">未判定</MetricTip>,
		align: "right",
		render: (b) =>
			b.undetermined ? (
				<span title={undetText(b.undetermined_reasons)}>
					<Badge tone="warning">{b.undetermined}</Badge>
					{b.undetermined_reasons?.rate_limited ? (
						<span className="ml-1 text-xs text-text-muted" data-testid="plan-stats-rate-limited">
							限流 {b.undetermined_reasons.rate_limited}
						</span>
					) : null}
				</span>
			) : (
				<Muted>0</Muted>
			),
	},
];

function BatchesPanel({ onSegments }: { onSegments: (batchId: string) => void }) {
	const [limit, setLimit] = useState("10");
	const q = usePlanStatsBatches(Number(limit));
	const [open, setOpen] = useState<string | null>(null);
	if (q.isLoading || q.isError || !q.data) return <QueryState q={q} testId="plan-stats-fail" />;
	const rows = q.data.rows;
	const latest = rows.find((r) => r.wall_s != null);
	return (
		<>
			{latest ? (
				<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="plan-stats-kpis">
					<KpiCard
						label={<MetricTip id="stats.wall_s">最近一批墙钟</MetricTip>}
						value={fmtS(latest.wall_s)}
						hint={`${tail(latest.batch_id)} · ${latest.ran} 次试跑 · 并发 ${latest.concurrency}`}
						tone="accent"
					/>
					<KpiCard
						label={<MetricTip id="stats.trial_p50_s">单次试跑 p50</MetricTip>}
						value={fmtS(latest.trial_p50_s)}
						hint={`平均 ${fmtS(latest.trial_mean_s)}`}
					/>
					<KpiCard
						label={<MetricTip id="stats.trial_p90_s">单次试跑 p90</MetricTip>}
						value={fmtS(latest.trial_p90_s)}
						hint={`最长 ${fmtS(latest.trial_max_s)} · 中位数会藏长尾`}
						tone="warning"
					/>
					<KpiCard
						label={<MetricTip id="stats.trial_sum_s">试跑耗时之和</MetricTip>}
						value={fmtS(latest.trial_sum_s)}
						hint={`÷ 墙钟 ＝ 等效并发 ${latest.parallelism ?? "—"}`}
					/>
				</div>
			) : null}
			<Block>
				<div className="flex flex-wrap items-center gap-3">
					<LabeledSelect
						label="最近"
						value={limit}
						onChange={setLimit}
						options={["10", "20", "50"].map((v) => ({ value: v, label: `${v} 批` }))}
					/>
					<Muted>点一行看分题族耗时和最慢的试跑；排队等待 ＝ 建批到开跑（前面的批次没跑完）</Muted>
					<span className="ml-auto text-xs text-text-muted">共 {rows.length} 批</span>
				</div>
				{rows.length ? (
					<PlanTable
						columns={BATCH_COLUMNS}
						rows={rows}
						rowKey={(r) => r.batch_id}
						onRowClick={(r) => setOpen(r.batch_id)}
						density="compact"
						rowTestId="plan-stats-batch-row"
					/>
				) : (
					<EmptyState
						title="还没有评测批次"
						desc="在「模拟场景」发起评测后，每一批的耗时都列在这里。"
					/>
				)}
			</Block>
			<BatchDrawer
				batchId={open}
				onClose={() => setOpen(null)}
				onSegments={(id) => {
					setOpen(null);
					onSegments(id);
				}}
			/>
		</>
	);
}

const FAMILY_COLUMNS: PlanColumn<PlanStatsFamily>[] = [
	{ key: "f", title: "题族", render: (f) => <span className="font-mono">{f.family}</span> },
	{ key: "n", title: "次数", align: "right", render: (f) => f.trials },
	{
		key: "p50",
		title: <MetricTip id="stats.family_p50_s">p50</MetricTip>,
		align: "right",
		render: (f) => fmtS(f.p50_s),
	},
	{
		key: "p90",
		title: <MetricTip id="stats.family_p90_s">p90</MetricTip>,
		align: "right",
		render: (f) => fmtS(f.p90_s),
	},
	{
		key: "max",
		title: <MetricTip id="stats.family_max_s">最长</MetricTip>,
		align: "right",
		render: (f) => fmtS(f.max_s),
	},
	{
		key: "mean",
		title: <MetricTip id="stats.family_mean_s">平均</MetricTip>,
		align: "right",
		render: (f) => fmtS(f.mean_s),
	},
	{
		key: "share",
		title: <MetricTip id="stats.family_share">占试跑耗时</MetricTip>,
		align: "right",
		render: (f) => `${fmtS(f.sum_s)}（${fmtPct(f.share)}）`,
	},
];

const SLOW_COLUMNS: PlanColumn<PlanStatsSlowTrial>[] = [
	{
		key: "s",
		title: "题目",
		className: "max-w-[200px]",
		render: (t) => (
			<span className="block truncate font-mono text-xs" title={t.scenario_id}>
				{t.scenario_id}
			</span>
		),
	},
	{ key: "f", title: "题族", render: (t) => <span className="font-mono">{t.family ?? "—"}</span> },
	{
		key: "i",
		title: "第几次",
		align: "right",
		render: (t) => `${(t.index ?? 0) + 1}${t.attempt ? `（补跑 ${t.attempt}）` : ""}`,
	},
	{
		key: "d",
		title: "耗时",
		align: "right",
		render: (t) => <span className="font-semibold">{fmtS(t.duration_s)}</span>,
	},
	{ key: "turns", title: "轮数", align: "right", render: (t) => t.num_turns ?? "—" },
	{
		key: "o",
		title: "结果",
		render: (t) => <OutcomeBadge r={{ outcome: t.outcome ?? "", reason: t.reason }} />,
	},
];

function BatchDrawer({
	batchId,
	onClose,
	onSegments,
}: {
	batchId: string | null;
	onClose: () => void;
	onSegments: (batchId: string) => void;
}) {
	const q = usePlanStatsBatch(batchId);
	const navigate = useNavigate();
	const [drill, setDrill] = useState<PlanStatsSlowTrial | null>(null);
	const d = q.data;
	return (
		<>
			<Drawer
				open={!!batchId}
				onClose={onClose}
				width="min(820px,95vw)"
				testId="plan-stats-batch-drawer"
				title={batchId ? `批次 ${tail(batchId)} 的耗时` : ""}
				description={
					d ? `${setName(d) ?? "自选"} · ${d.scenario_count} 题 · ${d.batch_id}` : undefined
				}
				badges={
					d ? (
						<Badge tone={BATCH_STATUS_TONE[d.status ?? ""] ?? "muted"}>
							{zh(BATCH_STATUS_ZH, d.status)}
						</Badge>
					) : null
				}
			>
				{q.isLoading ? (
					<LoadingInline text="统计这一批…" />
				) : q.isError || !d ? (
					<FailState
						reason={planErrorMessage(q.error)}
						onRetry={() => q.refetch()}
						retrying={q.isFetching}
					/>
				) : (
					<>
						<DrawerSection title="整批">
							<div
								className="grid grid-cols-2 gap-3 sm:grid-cols-4"
								data-testid="plan-stats-batch-summary"
							>
								<Fig
									label={<MetricTip id="stats.wall_s">墙钟（用户实际等的）</MetricTip>}
									value={fmtS(d.wall_s)}
									strong
								/>
								<Fig
									label={<MetricTip id="stats.queue_wait_s">排队等待</MetricTip>}
									value={fmtS(d.queue_wait_s)}
								/>
								<Fig
									label={<MetricTip id="stats.trial_sum_s">试跑耗时之和</MetricTip>}
									value={fmtS(d.trial_sum_s)}
								/>
								<Fig
									label={<MetricTip id="stats.parallelism">等效并发</MetricTip>}
									value={`${d.parallelism ?? "—"}（设 ${d.concurrency}）`}
								/>
								<Fig
									label={<MetricTip id="stats.trial_p50_s">单次 p50</MetricTip>}
									value={fmtS(d.trial_p50_s)}
								/>
								<Fig
									label={<MetricTip id="stats.trial_p90_s">单次 p90</MetricTip>}
									value={fmtS(d.trial_p90_s)}
								/>
								<Fig
									label={<MetricTip id="stats.trial_max_s">单次最长</MetricTip>}
									value={fmtS(d.trial_max_s)}
								/>
								<Fig
									label={<MetricTip id="stats.cost_usd">花费</MetricTip>}
									value={usd(d.cost_usd)}
								/>
							</div>
							<Muted>
								开跑 {at(d.started_at)} · 结束 {at(d.ended_at)} · 已跑 {d.ran}/{d.total_trials} 次
								{d.undetermined
									? ` · 未判定 ${d.undetermined}（${undetText(d.undetermined_reasons) || "—"}）`
									: ""}
								{d.reruns ? ` · 补跑过 ${d.reruns} 轮，墙钟含原跑到补跑之间的间隔` : ""}
							</Muted>
						</DrawerSection>
						<DrawerSection title="分题族耗时">
							{d.by_family.length ? (
								<PlanTable
									columns={FAMILY_COLUMNS}
									rows={d.by_family}
									rowKey={(f) => f.family}
									density="compact"
									rowTestId="plan-stats-family-row"
								/>
							) : (
								<Muted>这一批还没有跑完的试跑。</Muted>
							)}
						</DrawerSection>
						<DrawerSection title="最慢的 5 次试跑">
							{d.slowest.length ? (
								<>
									<PlanTable
										columns={SLOW_COLUMNS}
										rows={d.slowest}
										rowKey={(t) => t.trial_id}
										onRowClick={setDrill}
										density="compact"
										rowTestId="plan-stats-slow-row"
									/>
									<Muted>点一行打开这次试跑的运行轨迹（每轮各节点耗时在轨迹抽屉里）。</Muted>
								</>
							) : (
								<Muted>—</Muted>
							)}
						</DrawerSection>
						<div className="flex flex-wrap gap-2">
							<Button
								type="button"
								variant="outline"
								size="sm"
								onClick={() => onSegments(d.batch_id)}
								data-testid="plan-stats-to-segments"
							>
								看这批慢在哪（耗时分段）
							</Button>
							<Button
								type="button"
								variant="ghost"
								size="sm"
								onClick={() =>
									navigate({ to: "/plan-lab", search: { batch: d.batch_id }, hash: "eval" })
								}
							>
								打开这批的评测报告
							</Button>
						</div>
					</>
				)}
			</Drawer>
			<TrialDrawer
				trialId={drill?.trial_id ?? null}
				label={drill ? `${drill.scenario_id} · 第 ${(drill.index ?? 0) + 1} 次` : undefined}
				onClose={() => setDrill(null)}
			/>
		</>
	);
}

function Fig({
	label,
	value,
	strong = false,
}: { label: ReactNode; value: string; strong?: boolean }) {
	return (
		<div className="min-w-0 space-y-0.5">
			<div className="text-xs text-text-muted">{label}</div>
			<div className={`text-sm text-text-primary${strong ? " font-semibold" : ""}`}>{value}</div>
		</div>
	);
}

/* ── ② 耗时分段 ─────────────────────────────────────── */

const GROUP_COLOR: Record<string, string> = {
	agent: CHART.accent,
	sim: CHART.info,
	judge: CHART.success,
	cx: CHART.warning,
	unrecorded: CHART.inkMuted,
};
const GROUP_SHORT: Record<string, string> = {
	agent: "被测 agent",
	sim: "模拟客户",
	judge: "判官",
	cx: "CX",
	unrecorded: "未记账",
};

const SEG_COLUMNS: PlanColumn<PlanStatsSegmentRow>[] = [
	{
		key: "label",
		title: "环节",
		className: "max-w-[300px]",
		render: (r) =>
			r.group === "agent.sub" ? (
				<span className="block truncate pl-5 text-text-secondary" title={r.label}>
					{r.label}
				</span>
			) : (
				<span className="flex items-center gap-2 font-medium">
					<span
						className="h-2 w-2 shrink-0 rounded-[2px]"
						style={{ background: GROUP_COLOR[r.group] ?? CHART.inkMuted }}
					/>
					<span className="truncate" title={r.label}>
						{r.label}
					</span>
				</span>
			),
	},
	{
		key: "sec",
		title: <MetricTip id="stats.seg_seconds">合计耗时</MetricTip>,
		align: "right",
		render: (r) => fmtS(r.seconds),
	},
	{
		key: "share",
		title: <MetricTip id="stats.seg_share">占试跑耗时</MetricTip>,
		className: "w-44",
		render: (r) => (
			<span className="flex items-center gap-2">
				<span className="h-1.5 w-20 overflow-hidden rounded-full bg-surface-muted">
					<span
						className="block h-full rounded-full"
						style={{
							width: `${Math.min(100, (r.share ?? 0) * 100)}%`,
							background:
								GROUP_COLOR[r.group === "agent.sub" ? "agent" : r.group] ?? CHART.inkMuted,
							opacity: r.group === "agent.sub" ? 0.55 : 1,
						}}
					/>
				</span>
				<span className="font-mono text-xs">{fmtPct(r.share)}</span>
			</span>
		),
	},
	{
		key: "calls",
		title: <MetricTip id="stats.seg_calls">模型调用</MetricTip>,
		align: "right",
		render: (r) => (r.calls == null ? <Muted>—</Muted> : r.calls),
	},
	{
		key: "p",
		title: <MetricTip id="stats.seg_call_p">单次调用 p50 ／ p90</MetricTip>,
		align: "right",
		render: (r) =>
			r.call_p50_ms == null ? (
				<Muted>—</Muted>
			) : (
				`${fmtMs(r.call_p50_ms)} ／ ${fmtMs(r.call_p90_ms)}`
			),
	},
	{
		key: "cost",
		title: <MetricTip id="stats.seg_cost">实际花费</MetricTip>,
		align: "right",
		render: (r) => (r.cost_usd == null ? <Muted>—</Muted> : usd(r.cost_usd)),
	},
];

function SegmentsPanel({
	scope,
	onScope,
}: {
	scope: string | null;
	onScope: (v: string) => void;
}) {
	const batches = usePlanStatsBatches(20);
	const ran = useMemo(() => (batches.data?.rows ?? []).filter((b) => b.ran > 0), [batches.data]);
	const value = scope ?? ran[0]?.batch_id ?? (batches.data ? DAYS_ALL : null);
	const q = usePlanStatsSegments(value && value !== DAYS_ALL ? value : null, 7, value != null);
	if (batches.isLoading || batches.isError)
		return <QueryState q={batches} testId="plan-stats-fail" />;
	const picker = (
		<LabeledSelect
			label="范围"
			value={value ?? DAYS_ALL}
			onChange={onScope}
			options={[
				...ran.map((b) => ({ value: b.batch_id, label: batchLabel(b) })),
				{ value: DAYS_ALL, label: "近 7 天全部批次" },
			]}
		/>
	);
	if (q.isLoading || q.isError || !q.data)
		return (
			<>
				<Block>{picker}</Block>
				<QueryState q={q} testId="plan-stats-segments-fail" />
			</>
		);
	const d = q.data;
	const t = d.totals;
	const top = d.rows.filter((r) => r.group !== "agent.sub");
	const segs: BarSegment[] = top.map((r) => ({
		key: r.key,
		label: GROUP_SHORT[r.group] ?? r.label,
		value: Math.round(r.seconds ?? 0),
		color: GROUP_COLOR[r.group] ?? CHART.inkMuted,
	}));
	const share = (k: string) => d.rows.find((r) => r.key === k)?.share;
	return (
		<>
			<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="plan-stats-seg-kpis">
				<KpiCard
					label={<MetricTip id="stats.trial_sum_s">试跑耗时之和</MetricTip>}
					value={fmtS(t.trial_sum_s)}
					hint={`${t.trials ?? 0} 次试跑 · ${t.turns ?? 0} 轮 · ${t.batches ?? 0} 批`}
				/>
				<KpiCard
					label={<MetricTip id="stats.total_wall_s">整批墙钟</MetricTip>}
					value={fmtS(t.wall_s)}
					hint={`等效并发 ${t.parallelism ?? "—"}`}
					tone="accent"
				/>
				<KpiCard
					label={<MetricTip id="stats.agent_share">被测 agent 占比</MetricTip>}
					value={fmtPct(share("agent"))}
					tone="info"
				/>
				<KpiCard
					label={<MetricTip id="stats.unrecorded_share">未记账占比</MetricTip>}
					value={fmtPct(share("unrecorded"))}
					hint={fmtS(t.unrecorded_s)}
					tone={(share("unrecorded") ?? 0) > 0.1 ? "warning" : "muted"}
				/>
			</div>
			<Block>
				<div className="flex flex-wrap items-center gap-3">
					{picker}
					<Muted>占比的分母 ＝ 试跑耗时之和（每次试跑开始到结束相加，含补跑），不是墙钟</Muted>
				</div>
				<SegmentBar segments={segs} testId="plan-stats-seg-bar" />
				<PlanTable
					columns={SEG_COLUMNS}
					rows={[...d.rows, ...d.outside]}
					rowKey={(r) => r.key}
					density="compact"
					rowTestId="plan-stats-seg-row"
				/>
				<ul className="list-disc space-y-0.5 pl-5 text-xs text-text-muted">
					<li>
						被测 agent（整轮）取运行记录每轮耗时之和；各节点取账本模型调用耗时；非模型部分 ＝ 整轮 −
						模型调用
						{t.tools_scanned
							? "，工具调用取留痕 execute_tool 耗时"
							: "（只看单批时才扫留痕拆出工具调用）"}
						。
					</li>
					<li>
						未记账 ＝ 试跑耗时之和 − 被测 agent − 模拟客户 − 判官 −
						CX：调度、写盘、失败调用与重试等待等。CX 在
						10-20（#587）修好记账之前没进账本，之前批次的 CX 为 0、耗时落在未记账里。
					</li>
					{d.outside.length ? <li>重判在批后单独跑，不在试跑时间里，不算占比。</li> : null}
				</ul>
			</Block>
		</>
	);
}

/* ── ③ 单轮耗时 ─────────────────────────────────────── */

type TurnSrc = PlanStatsTurns["by_source"][number];
type TurnNode = PlanStatsTurns["nodes"][number];

const SRC_COLUMNS: PlanColumn<TurnSrc>[] = [
	{ key: "l", title: "来源", render: (r) => <span className="font-medium">{r.label}</span> },
	{
		key: "n",
		title: <MetricTip id="stats.turn_n">轮数</MetricTip>,
		align: "right",
		render: (r) => r.n,
	},
	{
		key: "p50",
		title: <MetricTip id="stats.turn_p50">p50</MetricTip>,
		align: "right",
		render: (r) => fmtMs(r.p50),
	},
	{
		key: "p90",
		title: <MetricTip id="stats.turn_p90">p90</MetricTip>,
		align: "right",
		render: (r) => <span className="font-semibold">{fmtMs(r.p90)}</span>,
	},
	{
		key: "max",
		title: <MetricTip id="stats.turn_max">最长</MetricTip>,
		align: "right",
		render: (r) => fmtMs(r.max),
	},
	{
		key: "mean",
		title: <MetricTip id="stats.turn_mean">平均</MetricTip>,
		align: "right",
		render: (r) => fmtMs(r.mean),
	},
];

const NODE_COLUMNS: PlanColumn<TurnNode>[] = [
	{
		key: "l",
		title: "节点",
		render: (r) => <span className={r.key === "turn" ? "font-semibold" : ""}>{r.label}</span>,
	},
	{
		key: "n",
		title: <MetricTip id="stats.node_n">出现轮数</MetricTip>,
		align: "right",
		render: (r) => r.n,
	},
	{
		key: "c",
		title: <MetricTip id="stats.node_calls_per_turn">每轮调用</MetricTip>,
		align: "right",
		render: (r) => (r.calls_per_turn == null ? <Muted>—</Muted> : r.calls_per_turn),
	},
	{
		key: "p50",
		title: <MetricTip id="stats.node_p50">p50</MetricTip>,
		align: "right",
		render: (r) => fmtMs(r.p50),
	},
	{
		key: "p90",
		title: <MetricTip id="stats.node_p90">p90</MetricTip>,
		align: "right",
		render: (r) => fmtMs(r.p90),
	},
	{
		key: "max",
		title: <MetricTip id="stats.node_max">最长</MetricTip>,
		align: "right",
		render: (r) => fmtMs(r.max),
	},
	{
		key: "mean",
		title: <MetricTip id="stats.node_mean">平均</MetricTip>,
		align: "right",
		render: (r) => fmtMs(r.mean),
	},
];

function TurnsPanel() {
	const [days, setDays] = useState("7");
	const [src, setSrc] = useState("all");
	const q = usePlanStatsTurns(Number(days), src === "all" ? null : src);
	const filters = (
		<div className="flex flex-wrap items-center gap-3">
			<LabeledSelect
				label="时间"
				value={days}
				onChange={setDays}
				options={[
					{ value: "1", label: "今天" },
					{ value: "7", label: "近 7 天" },
					{ value: "30", label: "近 30 天" },
				]}
			/>
			<LabeledSelect
				label="来源"
				value={src}
				onChange={setSrc}
				options={[
					{ value: "all", label: "全部" },
					{ value: "ask", label: "人工提问" },
					{ value: "eval", label: "评测" },
				]}
			/>
			<Muted>
				一轮 ＝ 被测 agent 收到客户一句话到写完运行记录；评测一次试跑约 4
				轮，另有模拟客户、判官；批次见 <PageJump to="eval-records" />
				，单轮见 <PageJump to="history" />
			</Muted>
		</div>
	);
	if (q.isLoading || q.isError || !q.data)
		return (
			<>
				<Block>{filters}</Block>
				<QueryState q={q} testId="plan-stats-turns-fail" />
			</>
		);
	const d = q.data;
	return (
		<>
			<Block>
				{filters}
				<PlanTable
					columns={SRC_COLUMNS}
					rows={d.by_source}
					rowKey={(r) => r.source}
					density="compact"
					rowTestId="plan-stats-src-row"
				/>
			</Block>
			<Block
				title="分节点（每轮该节点的模型调用耗时相加）"
				note={`来源：${src === "all" ? "全部" : src === "ask" ? "人工提问" : "评测"}；只统计出现该节点的轮`}
			>
				{d.nodes.length > 1 ? (
					<PlanTable
						columns={NODE_COLUMNS}
						rows={d.nodes}
						rowKey={(r) => r.key}
						density="compact"
						rowTestId="plan-stats-node-row"
					/>
				) : (
					<EmptyState title="这段时间没有运行记录" desc="换个时间范围或来源看看。" />
				)}
			</Block>
		</>
	);
}
